// CREUSER un insight.
//
// « Creuser » ne déplie rien : il demande à l'IA d'aller un cran plus loin.
// Elle relit tout le terrain du projet — pas seulement les preuves déjà citées —
// et propose UN nouvel insight, plus profond, que l'on peut ajouter au projet.
//
// Deux niveaux au plus : un insight creusé peut l'être encore une fois, pas
// davantage. Le serveur tient la même limite ; l'interface se contente de ne
// plus proposer le bouton.
import { Part } from './Part';
import { useState } from 'react';
import { useCreateInsight, useDeepenInsight } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Insight, InsightProposal } from '../api/types';
import { Button, ErrorBanner } from '../components/ui';
import { Icon } from '../components/icons';
import { ProblemTypeChip, RecoKindChip, SeverityChip } from '../taxonomy/Chips';
import './insights.css';

export const MAX_DEPTH = 2;

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function DigInsight({ insight, projectId }: { insight: Insight; projectId: string }) {
  const deepen = useDeepenInsight(projectId);
  const create = useCreateInsight(projectId);
  // `undefined` : rien demandé. `null` : l'IA n'a rien trouvé de plus.
  const [proposal, setProposal] = useState<InsightProposal | null | undefined>(undefined);
  const nextLevel = insight.depth + 1;

  if (insight.depth >= MAX_DEPTH) {
    return <p className="dig-note">Creusé sur {MAX_DEPTH} niveaux : on ne va pas plus loin.</p>;
  }

  async function dig() {
    try {
      const res = await deepen.mutateAsync(insight.id);
      setProposal(res.proposal);
    } catch {
      /* rendu par le bandeau */
    }
  }

  async function add(p: InsightProposal) {
    try {
      await create.mutateAsync({
        title: p.title,
        ...(p.summary ? { summary: p.summary } : {}),
        ...(p.why ? { why: p.why } : {}),
        ...(p.opportunity ? { opportunity: p.opportunity } : {}),
        ...(p.recommendation ? { recommendation: p.recommendation } : {}),
        ...(p.recommendation && p.recommendationKind ? { recommendationKind: p.recommendationKind } : {}),
        ...(p.severity ? { severity: p.severity } : {}),
        ...(p.problemType ? { problemType: p.problemType } : {}),
        ...(p.pillarId ? { pillarId: p.pillarId } : {}),
        origin: 'ia',
        verbatimIds: p.evidenceIds,
        // C'est ce lien qui donne sa profondeur au nouvel insight.
        parentId: insight.id,
      });
      setProposal(undefined);
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <div className="dig">
      <div className="dig-bar">
        <Button variant="ghost" loading={deepen.isPending} onClick={() => void dig()}>
          <Icon name="sparkles" size={14} />
          {proposal === undefined ? 'Creuser' : 'Creuser à nouveau'}
        </Button>
        <span className="dig-note">
          L’IA relit le terrain du projet et propose un insight plus profond · niveau {nextLevel} sur {MAX_DEPTH}
        </span>
      </div>
      {deepen.error ? <ErrorBanner>{errorMessage(deepen.error)}</ErrorBanner> : null}

      {proposal === null ? (
        <p className="zy-texte-muet">
          Rien de plus à tirer du terrain pour l’instant : aucun verbatim ne permet d’aller plus loin que cet insight.
        </p>
      ) : null}

      {proposal ? (
        <div className="dig-proposal">
          <p className="derive-head">
            <span className="t-mono">Proposition de l’IA · niveau {nextLevel}</span>
            {deepen.data?.mode === 'fake' ? <span className="derive-mode">démonstration locale, sans modèle</span> : null}
          </p>
          <p className="dig-title">
            {proposal.title}
            <SeverityChip value={proposal.severity} />
            <ProblemTypeChip type={proposal.problemType} />
          </p>
          <dl className="insight-parts">
            {proposal.summary ? <Part label="Constat" text={proposal.summary} /> : null}
            {proposal.why ? <Part label="Pourquoi" text={proposal.why} /> : null}
            {proposal.opportunity ? <Part label="Opportunité" text={proposal.opportunity} /> : null}
            {proposal.recommendation ? (
              <div>
                <dt>
                  Recommandation
                  <RecoKindChip kind={proposal.recommendationKind} />
                </dt>
                <dd>{proposal.recommendation}</dd>
              </div>
            ) : null}
          </dl>
          <ul className="derive-evidence">
            {proposal.evidence.map((e) => (
              <li key={e.id}>
                <span className={`dot dot-${e.sentiment ?? 'neu'}`} aria-hidden="true" />
                <span>
                  « {e.text} »
                  {e.speaker || e.sourceRef ? <span className="evidence-meta">{[e.speaker, e.sourceRef].filter(Boolean).join(' · ')}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          {create.error ? <ErrorBanner>{errorMessage(create.error)}</ErrorBanner> : null}
          <div className="dig-actions">
            <Button variant="accent" loading={create.isPending} onClick={() => void add(proposal)}>
              Ajouter au projet
            </Button>
            <Button variant="ghost" onClick={() => setProposal(undefined)}>
              Écarter
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

